import { AuthState, LoginResult, AuthResponse, UserInfo,PasskeyStartResponse,PasskeyFinishResponse } from '@rsmaids/shared/types/auth';
import { createApiError, ApiErrorCode } from '@rsmaids/shared/types/errors';
import { SessionTokens } from '@rsmaids/shared/types/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { startRegistration } from '@simplewebauthn/browser';
import { getPasskeyOrigin } from '@rsmaids/shared/utils/passkeyOrigin';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';


// Constants
const SESSION_STORAGE_KEY: string = 'session';
const SESSION_EXPIRY_DAYS: number = 90; // 90 days for automatic logout
const MILLISECONDS_PER_DAY: number = 24 * 60 * 60 * 1000;
const REQUEST_TIMEOUT_MS: number = 30000; // 30 second timeout for all requests

// Main RS Auth Client
export class rsAuthClient {
  private pendingRequests: Map<string, Promise<unknown>> = new Map();

  // Helper method to add timeout to promises
  private async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number = REQUEST_TIMEOUT_MS
  ): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout - please try again')), timeoutMs)
      ),
    ]);
  }

  // Helper method to deduplicate concurrent identical requests
  private async deduplicatedRequest<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // If same request is already pending, return that promise
    if (this.pendingRequests.has(key)) {
      console.log(`[rsAuthClient] Deduplicating request: ${key}`);
      return this.pendingRequests.get(key) as Promise<T>;
    }

    // Execute new request and store the promise
    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  // Helper method to call Firebase functions with consistent error handling
  private async callFirebaseFunction(functionName: string, data: Record<string, unknown>): Promise<AuthResponse> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, functionName);

      // Add timeout to function calls
      const result = await this.withTimeout(callable(data) as Promise<{ data: AuthResponse }>);
      return result.data as AuthResponse;
    } catch (error) {
      console.error(`[${functionName}] Error:`, error);
      throw createApiError(
        ApiErrorCode.UNKNOWN_ERROR,
        error instanceof Error ? error.message : 'Network error'
      );
    }
  }

  // Check if session has expired (90 days)
  private isSessionExpired(loginTimestamp: number): boolean {
    const currentTime = Date.now();
    const sessionAge = currentTime - loginTimestamp;
    const maxSessionAge = SESSION_EXPIRY_DAYS * MILLISECONDS_PER_DAY;
    return sessionAge > maxSessionAge;
  }

  // Simple login status check - Firebase Auth handles persistence
  async checkLoginStatus(): Promise<AuthState & { firebaseToken?: string }> {
    // Import error util
    // import { createApiError, ApiErrorCode } from '../types/errors';
    try {
      const sessionData = localStorage.getItem(SESSION_STORAGE_KEY);
      console.log('checkLoginStatus: sessionData from localStorage:', sessionData ? 'found' : 'not found');
      
      if (sessionData) {
        const tokens = JSON.parse(sessionData) as SessionTokens;
        console.log('checkLoginStatus: parsed tokens:', tokens);

        if (!tokens?.user?.email) {
          localStorage.removeItem(SESSION_STORAGE_KEY);
          return {
            isLoggedIn: false,
            isLoading: false,
            user: null,
            error: createApiError(ApiErrorCode.AUTH_INVALID_USER_TOKEN)
          };
        }

        // Check if session has expired (90 days)
        if (tokens.loginTimestamp && this.isSessionExpired(tokens.loginTimestamp)) {
          console.log('Session expired after 90 days, logging out automatically');
          await this.logout();
          return {
            isLoggedIn: false,
            isLoading: false,
            user: null,
            error: createApiError(ApiErrorCode.AUTH_SESSION_EXPIRED, 'Session expired after 90 days. Please log in again.')
          };
        }

        console.log('checkLoginStatus: returning logged in with user:', tokens.user);
        return {
          isLoggedIn: true,
          isLoading: false,
          user: tokens.user,
          firebaseToken: tokens.firebaseToken,
          error: null
        };
      }

      // No session data in localStorage, check Firebase auth state
      // This handles cases like passkey login where we don't store session data initially
      try {
        console.log('checkLoginStatus: No session data, checking Firebase auth');
        const auth = getAuth();
        const firebaseUser = auth.currentUser;
        console.log('checkLoginStatus: Firebase currentUser:', firebaseUser?.uid);

        if (firebaseUser) {
          console.log('checkLoginStatus: Found Firebase user but no session data, calling backend login');
          // Try to get fresh user data from backend
          const idToken = await firebaseUser.getIdToken();
          console.log('checkLoginStatus: Got ID token, calling this.login');
          const backendResult = await this.login(idToken);
          console.log('checkLoginStatus: Backend login result:', backendResult);

          if (backendResult.success && backendResult.user) {
            // Store session data for future checks
            const tokensToStore: SessionTokens = {
              user: backendResult.user,
              firebaseToken: backendResult.sessionToken || '',
              loginTimestamp: Date.now()
            };
            localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(tokensToStore));
            console.log('checkLoginStatus: Stored session data, returning logged in');

            return {
              isLoggedIn: true,
              isLoading: false,
              user: backendResult.user,
              firebaseToken: backendResult.sessionToken,
              error: null
            };
          } else {
            console.log('checkLoginStatus: Backend login failed:', backendResult);
          }
        } else {
          console.log('checkLoginStatus: No Firebase user found');
        }
      } catch (firebaseError) {
        console.warn('checkLoginStatus: Error checking Firebase auth:', firebaseError);
      }

      return {
        isLoggedIn: false,
        isLoading: false,
        user: null,
        error: createApiError(ApiErrorCode.AUTH_USER_NOT_FOUND)
      };
    } catch (error) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return {
        isLoggedIn: false,
        isLoading: false,
        user: null,
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : undefined)
      };
    }
  }

  // Login function - stores Firebase Auth token and user data
  async login(idToken: string, _password?: string): Promise<LoginResult> {
    try {
      // For token-based login (passkey), call backend with ID token
      console.log('login: Calling backend with ID token for user data');
      const authResult = await this.callFirebaseFunction('login', { idToken });

      console.log('Backend token login result:', authResult);

      if (authResult.success && authResult.user) {
        // Map the response user to UserInfo format
        const userInfo: UserInfo = {
          id: authResult.user.uid || authResult.user.id || '',
          uid: authResult.user.uid,
          name: authResult.user.name,
          email: authResult.user.email,
          role: authResult.user.role,
          phone: authResult.user.phone || '',
          address: authResult.user.address || '',
          position: authResult.user.position,
          jobTitle: authResult.user.jobTitle,
          firstName: authResult.user.firstName,
          lastName: authResult.user.lastName,
          avatarUrl: authResult.user.avatarUrl,
          photoURL: authResult.user.photoURL,
          profileImage: authResult.user.profileImage,
          wixContactId: authResult.user.wixContactId,
          helcimCustomerId: authResult.user.helcimCustomerId,
          preferences: (authResult.user as UserInfo & { preferences?: { dashboardShortcuts?: string[] } }).preferences
        };

        const tokensToStore: SessionTokens = {
          user: userInfo,
          firebaseToken: idToken,
          loginTimestamp: Date.now()
        };
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(tokensToStore));

        return {
          success: true,
          userID: userInfo.id,
          user: userInfo,
          sessionToken: idToken
        };
      } else {
        return {
          success: false,
          userID: '',
          error: authResult.error || createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, 'Token login failed')
        };
      }
    } catch (error) {
      console.error('Token login error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, 'Token login failed')
      };
    }
  }

  // Profile + role of the account that is already signed in with Firebase Auth. The login screen signs in first (Firebase
  // checks the password); this call carries no email or password — the server learns who is calling from that sign-in.
  async loginBackend(): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('login', {});

      console.log('Backend login result:', authResult);

      // The server fixed the role on this sign-in token: fetch a fresh one now so Firestore rules see the role at once
      if (authResult.claimsUpdated) {
        await getAuth().currentUser?.getIdToken(true).catch((tokenError) => console.error('Could not refresh the sign-in token:', tokenError));
      }

      if (authResult.success && authResult.user) {
        // Map the response user to UserInfo format
        const userInfo: UserInfo = {
          id: authResult.user.uid || authResult.user.id || '',
          name: authResult.user.name,
          email: authResult.user.email,
          role: authResult.user.role,
          phone: authResult.user.phone || '',
          address: authResult.user.address || '',
          position: authResult.user.position,
          jobTitle: authResult.user.jobTitle,
          firstName: authResult.user.firstName,
          lastName: authResult.user.lastName,
          avatarUrl: authResult.user.avatarUrl,
          photoURL: authResult.user.photoURL,
          profileImage: authResult.user.profileImage,
          wixContactId: authResult.user.wixContactId,
          helcimCustomerId: authResult.user.helcimCustomerId
        };

        // Store tokens and user info for successful login
        const tokensToStore: SessionTokens = {
          user: userInfo,
          firebaseToken: '', // Firebase Auth keeps its own session; nothing sign-in related is stored here
          loginTimestamp: Date.now()
        };

        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(tokensToStore));

        return {
          success: true,
          userID: userInfo.id,
          user: userInfo
        };
      } else {
        // Handle login errors
        if (authResult.requiresEmailVerification) {
          return {
            success: false,
            userID: authResult.user?.uid || authResult.user?.id || '',
            error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.error || 'Email verification required'),
            requiresEmailVerification: true,
            email: authResult.email
          };
        }

        return {
          success: false,
          userID: '',
          error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.error || 'Login failed')
        };
      }
    } catch (error) {
      console.error('Backend login error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : 'Network error')
      };
    }
  }

  // Resend email verification
  async resendVerification(email: string): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('resendVerification', { email });

      if (authResult.success) {
        return {
          success: true,
          userID: authResult.user?.uid || authResult.user?.id || '',
          message: authResult.message || 'Verification email sent successfully'
        };
      } else {
        return {
          success: false,
          userID: '',
          error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.error || 'Failed to resend verification email')
        };
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : 'Network error')
      };
    }
  }

  // Send passkey verification code
  async sendPasskeyVerificationCode(email: string): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('sendPasskeyVerificationCode', { email });

      if (authResult.success) {
        return {
          success: true,
          userID: '',
          message: authResult.message || 'Verification code sent successfully'
        };
      } else {
        return {
          success: false,
          userID: '',
          error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.error || 'Failed to send verification code')
        };
      }
    } catch (error) {
      console.error('Send passkey verification code error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : 'Network error')
      };
    }
  }

  // Backend registration function - Firebase-only registration
  async registerBackend(email: string, password: string, userInfo?: { firstName?: string; lastName?: string; phone?: string }): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('registerBackend', {
        email,
        password,
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        phone: userInfo?.phone
      });

      if (authResult.success) {
        return {
          success: true,
          userID: authResult.user?.uid || authResult.user?.id || '',
          requiresEmailVerification: authResult.requiresEmailVerification,
          email: authResult.email,
          message: authResult.message,
          hasEmployeeCode: authResult.hasEmployeeCode,
          verificationType: authResult.verificationType
        };
      } else {
        return {
          success: false,
          userID: '',
          error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.error || authResult.message || 'Registration failed')
        };
      }
    } catch (error) {
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : undefined)
      };
    }
  }

  // Password reset function - calls Firebase function
  async resetPassword(email: string): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('resetPassword', { email });

      return {
        success: authResult.success,
        error: authResult.error ? createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.error).message : undefined,
        userID: ''
      };
    } catch (error) {
      let message = 'Password reset failed';
      if (error instanceof Error && error.message) {
        message = error.message;
      }
      return {
        success: false,
        error: message,
        userID: ''
      };
    }
  }

  // Handle logout - clears Firebase session
  async logout(): Promise<{ success: boolean; error?: string }> {
    try {
      // Clear local storage session
      localStorage.removeItem(SESSION_STORAGE_KEY);
      
      // Note: Firebase Auth logout should be handled by the calling component
      // using Firebase's signOut() method, as this service doesn't have direct
      // access to the Firebase Auth instance
      
      console.log('User logged out successfully (90-day session management)');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: createApiError(
          ApiErrorCode.SERVER_ERROR,
          error instanceof Error ? error.message : 'Logout failed'
        ).message
      };
    }
  }

  /**
   * Verify email verification code
   * @param email - User email
   * @param code - 6-digit verification code
   * @returns Promise<LoginResult>
   */
  async verifyEmailCode(email: string, code: string): Promise<LoginResult> {
    console.log('[verifyEmailCode] Starting verification with:', { email, code, codeLength: code.length });

    try {
      const authResult = await this.callFirebaseFunction('verifyEmail', { email, verificationCode: code });

      if (authResult.success) {
        // Map the response user to UserInfo format
        const userInfo: UserInfo | undefined = authResult.user ? {
          id: authResult.user.uid || authResult.user.id || '',
          name: authResult.user.name,
          email: authResult.user.email,
          role: authResult.user.role,
          phone: authResult.user.phone || '',
          address: authResult.user.address || '',
          position: authResult.user.position,
          jobTitle: authResult.user.jobTitle,
          firstName: authResult.user.firstName,
          lastName: authResult.user.lastName,
          customToken: authResult.user.customToken,
          avatarUrl: authResult.user.avatarUrl,
          photoURL: authResult.user.photoURL,
          profileImage: authResult.user.profileImage,
          wixContactId: authResult.user.wixContactId,
          helcimCustomerId: authResult.user.helcimCustomerId
        } : undefined;

        return {
          success: true,
          userID: authResult.user?.uid || authResult.user?.id || '',
          message: authResult.message || 'Email verified successfully',
          user: userInfo
        };
      } else {
        return {
          success: false,
          userID: '',
          error: authResult.error || 'Email verification failed'
        };
      }
    } catch (error) {
      console.error('[rsAuthClient] Email verification error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(
          ApiErrorCode.SERVER_ERROR,
          error instanceof Error ? error.message : 'Email verification failed'
        ).message
      };
    }
  }

  /**
   * Verify employee role assignment code
   * @param email - Employee email
   * @param code - 8-character employee code
   * @returns Promise<LoginResult>
   */
  async verifyEmployeeCode(email: string, code: string): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('verifyEmployeeCode', { email, employeeCode: code });

      if (authResult.success) {
        // Map the response user to UserInfo format if present
        const userInfo: UserInfo | undefined = authResult.user ? {
          id: authResult.user.uid || authResult.user.id || '',
          name: authResult.user.name,
          email: authResult.user.email,
          role: authResult.user.role,
          phone: authResult.user.phone || '',
          address: authResult.user.address || '',
          position: authResult.user.position,
          jobTitle: authResult.user.jobTitle,
          firstName: authResult.user.firstName,
          lastName: authResult.user.lastName,
          customToken: authResult.user.customToken,
          avatarUrl: authResult.user.avatarUrl,
          photoURL: authResult.user.photoURL,
          profileImage: authResult.user.profileImage,
          wixContactId: authResult.user.wixContactId,
          helcimCustomerId: authResult.user.helcimCustomerId
        } : undefined;

        return {
          success: true,
          userID: authResult.user?.uid || authResult.user?.id || '',
          message: authResult.message || 'Employee code verified successfully',
          user: userInfo
        };
      } else {
        return {
          success: false,
          userID: '',
          error: authResult.message || 'Employee code verification failed'
        };
      }
    } catch (error) {
      console.error('[rsAuthClient] Employee code verification error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(
          ApiErrorCode.SERVER_ERROR,
          error instanceof Error ? error.message : 'Employee code verification failed'
        ).message
      };
    }
  }

  /**
   * Create employee role assignment code
   * @param employeeEmail - Employee email to assign role to
   * @param role - Role to assign
   * @returns Promise<LoginResult>
   */
  async createEmployeeCode(employeeEmail: string, role: string): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('createEmployeeCode', { employeeEmail, assignedRole: role });

      if (authResult.success) {
        return {
          success: true,
          userID: '',
          message: authResult.message || 'Employee code created successfully'
        };
      } else {
        return {
          success: false,
          userID: '',
          error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.message || 'Failed to create employee code')
        };
      }
    } catch (error) {
      console.error('[rsAuthClient] Create employee code error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : 'Failed to create employee code')
      };
    }
  }

  /**
   * List active role codes
   * @returns Promise<LoginResult>
   */
  async listRoleCodes(): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('listRoleCodes', {});

      if (authResult.success) {
        return {
          success: true,
          userID: '',
          message: authResult.message || 'Role codes retrieved successfully',
          codes: authResult.codes // Backend returns codes array
        };
      } else {
        return {
          success: false,
          userID: '',
          error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.message || 'Failed to list role codes')
        };
      }
    } catch (error) {
      console.error('[rsAuthClient] List role codes error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : 'Failed to list role codes')
      };
    }
  }

  /**
   * Revoke role code
   * @param code - Code to revoke
   * @returns Promise<LoginResult>
   */
  async revokeRoleCode(code: string): Promise<LoginResult> {
    try {
      const authResult = await this.callFirebaseFunction('revokeRoleCode', { code });

      if (authResult.success) {
        return {
          success: true,
          userID: '',
          message: authResult.message || 'Role code revoked successfully'
        };
      } else {
        return {
          success: false,
          userID: '',
          error: createApiError(ApiErrorCode.AUTH_INVALID_CREDENTIALS, authResult.message || 'Failed to revoke role code')
        };
      }
    } catch (error) {
      console.error('[rsAuthClient] Revoke role code error:', error);
      return {
        success: false,
        userID: '',
        error: createApiError(ApiErrorCode.UNKNOWN_ERROR, error instanceof Error ? error.message : 'Failed to revoke role code')
      };
    }
  }

  /**
   * Start passkey login
   * @param identifier - User email
   * @returns Promise<PasskeyStartResponse>
   */
  async startPasskeyLogin(identifier: string): Promise<PasskeyStartResponse> {
    const requestKey = `passkey-login-start-${identifier}`;
    
    return this.deduplicatedRequest(requestKey, async () => {
      try {
        const origin = getPasskeyOrigin();
        const authResult = await this.callFirebaseFunction('startPasskeyLogin', { identifier, origin });
        return authResult as PasskeyStartResponse;
      } catch (error) {
        console.error('[rsAuthClient] Start passkey login error:', error);
        throw error;
      }
    });
  }

  /**
   * Finish passkey login
   * @param signedChallenge - Signed challenge data
   * @returns Promise<PasskeyFinishResponse>
   */
  async finishPasskeyLogin(signedChallenge: Record<string, unknown>): Promise<PasskeyFinishResponse> {
    try {
      const origin = getPasskeyOrigin();
      const authResult = await this.callFirebaseFunction('finishPasskeyLogin', { signedChallenge, origin });
      
      // Map the response user to UserInfo format if present
      let userInfo: UserInfo | undefined;
      if (authResult.user) {
        userInfo = {
          id: authResult.user.uid || authResult.user.id || '',
          name: authResult.user.name,
          email: authResult.user.email,
          role: authResult.user.role,
          phone: authResult.user.phone || '',
          address: authResult.user.address || '',
          position: authResult.user.position,
          jobTitle: authResult.user.jobTitle,
          firstName: authResult.user.firstName,
          lastName: authResult.user.lastName,
          customToken: authResult.user.customToken,
          avatarUrl: authResult.user.avatarUrl,
          photoURL: authResult.user.photoURL,
          profileImage: authResult.user.profileImage,
          wixContactId: authResult.user.wixContactId,
          helcimCustomerId: authResult.user.helcimCustomerId
        };
      }
      
      return {
        success: authResult.success,
        user: userInfo,
        message: authResult.message
      } as PasskeyFinishResponse;
    } catch (error) {
      console.error('[rsAuthClient] Finish passkey login error:', error);
      throw error;
    }
  }

  /**
   * Start passkey registration
   * @param identifier - User email
   * @returns Promise<PasskeyStartResponse>
   */
  async startPasskeyRegistration(identifier: string): Promise<PasskeyStartResponse> {
    const requestKey = `passkey-registration-start-${identifier}`;
    
    return this.deduplicatedRequest(requestKey, async () => {
      try {
        const origin = getPasskeyOrigin();
        const authResult = await this.callFirebaseFunction('startPasskeyRegistration', { identifier, origin });
        return authResult as PasskeyStartResponse;
      } catch (error) {
        console.error('[rsAuthClient] Start passkey registration error:', error);
        throw error;
      }
    });
  }

  /**
   * Finish passkey registration
   * @param signedChallenge - Signed challenge data
   * @returns Promise<PasskeyFinishResponse>
   */
  async finishPasskeyRegistration(signedChallenge: Record<string, unknown>): Promise<PasskeyFinishResponse> {
    try {
      const origin = getPasskeyOrigin();
      const authResult = await this.callFirebaseFunction('finishPasskeyRegistration', { signedChallenge, origin });
      return authResult as PasskeyFinishResponse;
    } catch (error) {
      console.error('[rsAuthClient] Finish passkey registration error:', error);
      throw error;
    }
  }

  /**
   * Register a new passkey (for adding additional passkeys to existing account)
   * @param identifier - Optional user email (if available)
   * @param deviceInfo - Device information
   * @returns Promise<AuthResponse>
   */
  async registerWithPasskey(identifier?: string, deviceInfo?: Record<string, unknown>): Promise<AuthResponse> {
    try {
      const origin = getPasskeyOrigin();
      
      // Start registration
      const startResult = await this.callFirebaseFunction('startPasskeyRegistration', { 
        identifier, 
        origin 
      }) as PasskeyStartResponse;
      
      if (!startResult.success || !startResult.options) {
        return startResult;
      }

      // Use SimpleWebAuthn's startRegistration helper which handles all conversions
      const credential = await startRegistration({ 
        optionsJSON: startResult.options as PublicKeyCredentialCreationOptionsJSON 
      });

      // Finish registration
      const finishResult = await this.callFirebaseFunction('finishPasskeyRegistration', { 
        signedChallenge: credential,
        origin,
        deviceInfo
      });

      return finishResult;
    } catch (error) {
      console.error('[rsAuthClient] Register with passkey error:', error);
      throw error;
    }
  }

  /**
   * Register passkey for unauthenticated user using email verification code
   * @param email - User's email address
   * @param verificationCode - Email verification code
   * @param deviceInfo - Optional device information
   * @returns Promise<AuthResponse>
   */
  async registerWithPasskeyAndCode(email: string, verificationCode: string, deviceInfo?: Record<string, unknown>): Promise<AuthResponse> {
    try {
      const origin = getPasskeyOrigin();
      
      // Start registration with verification code
      const startResult = await this.callFirebaseFunction('startPasskeyRegistrationWithCode', { 
        email,
        verificationCode,
        origin 
      }) as PasskeyStartResponse;
      
      if (!startResult.success || !startResult.options) {
        return startResult;
      }

      // Use SimpleWebAuthn's startRegistration helper which handles all conversions
      const credential = await startRegistration({ 
        optionsJSON: startResult.options as PublicKeyCredentialCreationOptionsJSON 
      });

      // Finish registration
      const finishResult = await this.callFirebaseFunction('finishPasskeyRegistration', { 
        signedChallenge: credential,
        origin,
        deviceInfo
      });

      return finishResult;
    } catch (error) {
      console.error('[rsAuthClient] Register with passkey and code error:', error);
      throw error;
    }
  }

  /**
   * List all passkeys for the current user
   * @returns Promise<AuthResponse>
   */
  async listUserPasskeys(): Promise<AuthResponse> {
    const requestKey = 'list-passkeys';
    
    return this.deduplicatedRequest(requestKey, async () => {
      try {
        const authResult = await this.callFirebaseFunction('listUserPasskeys', {});
        return authResult;
      } catch (error) {
        console.error('[rsAuthClient] List passkeys error:', error);
        throw error;
      }
    });
  }

  /**
   * Revoke/delete a passkey
   * @param passkeyId - The ID of the passkey to revoke
   * @returns Promise<AuthResponse>
   */
  async revokePasskey(passkeyId: string): Promise<AuthResponse> {
    try {
      const authResult = await this.callFirebaseFunction('revokePasskey', { passkeyId });
      return authResult;
    } catch (error) {
      console.error('[rsAuthClient] Revoke passkey error:', error);
      throw error;
    }
  }

  /**
   * Update passkey nickname
   * @param passkeyId - The ID of the passkey to update
   * @param nickname - The new nickname
   * @returns Promise<AuthResponse>
   */
  async updatePasskeyNickname(passkeyId: string, nickname: string): Promise<AuthResponse> {
    try {
      const authResult = await this.callFirebaseFunction('updatePasskeyNickname', { 
        passkeyId, 
        nickname 
      });
      return authResult;
    } catch (error) {
      console.error('[rsAuthClient] Update passkey nickname error:', error);
      throw error;
    }
  }

  async updateUserPreferences(preferences: { dashboardShortcuts?: string[]; notifications?: Record<string, unknown> }): Promise<AuthResponse> {
    try {
      const authResult = await this.callFirebaseFunction('updateUserPreferences', { 
        preferences 
      });
      return authResult;
    } catch (error) {
      console.error('[rsAuthClient] Update user preferences error:', error);
      throw error;
    }
  }

  async updateEmployeeNotifications(employeeEmail: string, notificationsEnabled: boolean, role?: string, staffId?: string): Promise<AuthResponse> {
    try {
      const authResult = await this.callFirebaseFunction('updateEmployeeNotifications', { 
        employeeEmail,
        notificationsEnabled,
        ...(role ? { role } : {}),
        ...(staffId ? { staffId } : {})
      });
      return authResult;
    } catch (error) {
      console.error('[rsAuthClient] Update employee notifications error:', error);
      throw error;
    }
  }

  // No token refresh logic needed for Firebase Auth client
}

// Create and export a singleton instance
export const rsAuth = new rsAuthClient();
